import { API_ENDPOINTS } from "@features/tickets/config/constants";
import type {
  TicketInput,
  TicketPatchInput,
  TicketRejectSolutionInput,
} from "@features/tickets/types/ticket.types";
import api from "@lib/api/client";
import { queryKeys } from "@lib/api/queryKeys";
import { useToast } from "@lib/hooks/useToast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export function useCreateTicket() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: TicketInput) => {
      const response = await api.post(API_ENDPOINTS.TICKETS.LIST, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      toast.success("Ticket créé avec succès");
    },
    onError: () => {
      toast.error("Erreur lors de la création du ticket");
    },
  });
}

export function useUpdateTicket() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number | string; data: TicketPatchInput }) => {
      const response = await api.patch(API_ENDPOINTS.TICKETS.DETAIL(id), data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(variables.id) });
      toast.success("Ticket mis à jour");
    },
    onError: () => {
      toast.error("Erreur lors de la mise à jour du ticket");
    },
  });
}

export function useTicketTransition() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      endpoint,
      data,
    }: {
      id: number | string;
      endpoint: string;
      data?: Record<string, unknown>;
    }) => {
      const response = await api.post(endpoint, data ?? {});
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(variables.id) });
      toast.success("Transition effectuée");
    },
    onError: () => {
      toast.error("Erreur lors de la transition");
    },
  });
}

export function useCreateTicketComment() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: { ticket: number | string; content: string }) => {
      const response = await api.post(API_ENDPOINTS.TICKET_COMMENTS.CREATE, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.comments.all(variables.ticket) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(variables.ticket) });
      toast.success("Commentaire ajouté");
    },
    onError: () => {
      toast.error("Erreur lors de l'ajout du commentaire");
    },
  });
}

export function useValidateSolution() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id: number | string) => {
      const response = await api.post(API_ENDPOINTS.TICKETS.VALIDATE_SOLUTION(id), {});
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(variables) });
      toast.success("Solution validée");
    },
    onError: () => {
      toast.error("Erreur lors de la validation de la solution");
    },
  });
}

export function useRejectSolution() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number | string; data: TicketRejectSolutionInput }) => {
      const response = await api.post(API_ENDPOINTS.TICKETS.REJECT_SOLUTION(id), data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(variables.id) });
      toast.success("Solution rejetée");
    },
    onError: () => {
      toast.error("Erreur lors du rejet de la solution");
    },
  });
}

export function useDeleteAttachment() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, ticketId }: { id: number | string; ticketId: number | string }) => {
      // TODO: backend DELETE endpoint for attachments is pending — the
      // TicketAttachmentService::deleteAttachment exists but no route exposes it yet.
      const response = await api.delete(API_ENDPOINTS.TICKET_ATTACHMENTS.DELETE(id));
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(variables.ticketId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.attachments.all(variables.ticketId),
      });
      toast.success("Pièce jointe supprimée");
    },
    onError: () => {
      toast.error("Erreur lors de la suppression de la pièce jointe");
    },
  });
}

export function useUploadTicketAttachment() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({
      ticketId,
      file,
      onProgress,
    }: {
      ticketId: number | string;
      file: File;
      onProgress?: (percent: number) => void;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(API_ENDPOINTS.TICKET_ATTACHMENTS.UPLOAD(ticketId), formData, {
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(variables.ticketId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.attachments.all(variables.ticketId),
      });
      toast.success("Pièce jointe ajoutée");
    },
    onError: () => {
      toast.error("Erreur lors de l'ajout de la pièce jointe");
    },
  });
}

export function useDeleteTicket() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id: number | string) => {
      const response = await api.delete(API_ENDPOINTS.TICKETS.DELETE(id));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      toast.success("Ticket supprimé");
    },
    onError: () => {
      toast.error("Erreur lors de la suppression du ticket");
    },
  });
}
